import {
	Component,
	ComponentInterface,
	Element,
	Event,
	EventEmitter,
	h,
	Host,
	Listen,
	Method,
	Prop,
	State,
	Watch,
} from '@stencil/core';
import axios from 'axios';
import { CardContainer } from './components/card-container';
import { isIe } from '../../../common/_helpers/browsers';
import { RevealAnimationStates } from '../_types/animations';
import {
	FundCardLayoutMode,
	DataPointsType,
	UserTypes,
	Regions,
	Appearance,
	FundType,
	EsgVisibilityStyle,
	ApiComponentName,
} from '../_types/fund-card-api';

import { AjaxFundData, FundData } from '../_interfaces/fund-card/fund-data';
import { baseApiComponents, fundCardConfig } from './fund-card-api-config';
import { FundCardInterface } from '../_interfaces/fund-card/fund-card';
import { calculateChildTagName, getChildComponent } from '../_helpers/prefixes';
import { dsConsole } from '../_helpers/console';
import {
	moveInlineStylesToHead,
	removeInlineStylesFromHead,
} from '../_helpers/content-security-policy';

const EsgDependentDataPoints: DataPointsType[] = ['esg', 'carbs'];

@Component({
	tag: 'ds-fund-card-api',
})
export class FundCardApi implements ComponentInterface, FundCardInterface {
	// NOTE: One not immediately obvious purpose of this web component is to ensure there is a good fallback solution for
	// IE11 for the Tooltip web components inside it. See the SCSS file for details (it looks for the presence of the CSS
	// class 'hydrated' which is added when the web component is rendered.

	@Prop() showIsaReady: boolean;
	@Element() el: HTMLDsFundCardApiElement;
	esgData: HTMLDivElement;
	hideEsgDataTimout: ReturnType<typeof setTimeout>;
	@State() layout: FundCardLayoutMode = FundCardLayoutMode.Standard;
	@State() esgVisibility: EsgVisibilityStyle = 'none';

	@State() data: FundData;

	// Initialise must be set to true for the AJAX request to fire
	// Once the data has been fetched, it will be stored in this.data
	// this.reveal must also be set to true for the card to appear
	@Prop({ reflect: true }) initialise = false;

	// The card is hidden by default and setting this to true reveals it, with optional animation
	// this.initialise must also be true for the card to show
	@Prop({ reflect: true }) reveal = false;

	@Prop() showDataPoints: boolean;
	@Prop() showAddToCompare: boolean;

	@Prop() inverted: string;
	@Prop() bgColor: string;
	@Prop() dataLinkEvent: string;
	@Prop() appearance: Appearance;
	@Prop() activeDataPoints: DataPointsType;

	@Prop() fundId: number;
	@Prop() fundUrl: string; // The URL of the product page for the fund

	@Prop() performanceLabel: string;
	@Prop() sustainabilityLabel: string;
	@Prop() buttonTranslations: string;
	@Prop() contentTranslations: string;
	@Prop() riskImages: string;

	@Prop() revealAnimation: RevealAnimationStates = 'on';
	@Prop({ reflect: true }) animateHover = true;
	@Prop() index: number;
	isIE = isIe();

	@Prop() fundType: FundType;
	@Prop() domicile: string;
	@Prop() domicileCode: string;
	@Prop() targetSite: string;
	@Prop() locale: string;
	@Prop() region: Regions;
	@Prop() userType: UserTypes;
	@Prop() isProduction = false;
	@Prop() isPreview = false;
	@Prop() xApiKey: string;
	@Prop() stageApiKey: string;
	@Prop() appSubType: string;
	@Prop() buyButtonText: string;
	@Prop() buyButtonAriaLabel: string;
	@Prop() buyFundUrl: string;
	@Prop() showBuyButton = false;
	@Prop() modalId: string;

	hasFetched = false; // Private property to keep track of if fetching has occurred or not (including if there was an error)
	esgAjaxRequest; // Private property to keep track of in an individual ESG AJAX request was already fired or not
	stagingPPApi = false; // Private property to keep track of if we wish to test the product pages staging api

	@Event()
	loadError: EventEmitter<{ error: typeof Error; element: HTMLElement }>;

	@Event()
	loadSuccess: EventEmitter<{ data: FundData; element: HTMLElement }>;

	componentWillLoad(): void {
		this.data = {
			riskImages: this.riskImages ? JSON.parse(this.riskImages) : null,
			showDataPoints: this.showDataPoints,
			showAddToCompare: this.showAddToCompare,
			activeDataPoints: this.activeDataPoints,
			bgColor: this.bgColor,
			inverted: this.inverted,
			fundId: this.fundId,
			performanceLabel: this.performanceLabel,
			sustainabilityLabel: this.sustainabilityLabel,
			fundUrl: this.fundUrl,
			fundType: this.fundType,
			domicile: this.domicile,
			domicileCode: this.domicileCode,
			locale: this.locale,
			buttonTranslations: JSON.parse(this.buttonTranslations),
			contentTranslations: JSON.parse(this.contentTranslations),
			isProduction: this.isProduction,
			isPreview: this.isPreview,
		};

		if (this.initialise) this.fetchFundCard();

		this.stagingPPApi = this.getIsStagingPPApi();
	}

	componentDidLoad(): void {
		this.setHoverAnimation();
		setTimeout(() => {
			moveInlineStylesToHead(this.el.id);
		}, 600);
	}

	componentDidUpdate(): void {
		setTimeout(() => {
			moveInlineStylesToHead(this.el.id);
		}, 600);
	}

	disconnectedCallback() {
		removeInlineStylesFromHead(this.el.id);
	}

	@Watch('initialise')
	onInitialiseStateChange(newValue, oldValue) {
		if (oldValue !== newValue && newValue === true) {
			this.fetchFundCard();
		}
	}

	@Watch('domicile')
	onDomicileStateChange(newValue) {
		this.data.domicile = newValue;
	}

	@Watch('fundType')
	onFundTypeStateChange(newValue) {
		this.data.fundType = newValue;
	}

	isEsgRequestNeeded(): boolean {
		return EsgDependentDataPoints.includes(this.activeDataPoints);
	}

	doAjaxRequests(): Promise<AjaxFundData> {
		const apiComponents: ApiComponentName[] = this.isEsgRequestNeeded()
			? [...baseApiComponents, 'esgAnalytics']
			: [...baseApiComponents];

		if (this.locale === 'nl_NL') {
			apiComponents.push('riskIndicator');
		}

		const ajaxRequests = apiComponents.map((component) =>
			axios.get(this.generateUrl(component), this.generateRequestOptions())
		);

		const mappedResponses: AjaxFundData = {};
		return Promise.all(ajaxRequests).then((responses) => {
			responses.map((response, i) => {
				mappedResponses[apiComponents[i]] = response.data.productData;
			});
			return mappedResponses;
		});
	}

	/**
	 * Generate URL for component
	 * @param component
	 */
	generateUrl(component: ApiComponentName) {
		const baseUrl = this.getBaseUrl();
		// For preview environments we call the preview end point that will be used to show unlaunched funds.
		// https://api.blackrock.com/product-data/v1/us/get-product-data?appSubType=ISHARES&appType=PRODUCT_PAGE&component=keyfundfacts&userType=individual&targetSite=us-ishares&locale=en_US&portfolioId=239726
		return this.isPreview
			? `${baseUrl}/product-data/preview/v1/${this.region}/get-product-data?appSubType=${this.appSubType}&appType=PRODUCT_PAGE&component=${component}&userType=${this.userType}&targetSite=${this.targetSite}&locale=${this.locale}&portfolioId=${this.fundId}`
			: `${baseUrl}/product-data/v1/${this.region}/get-product-data?appSubType=${this.appSubType}&appType=PRODUCT_PAGE&component=${component}&userType=${this.userType}&targetSite=${this.targetSite}&locale=${this.locale}&portfolioId=${this.fundId}`;
	}

	/**
	 * Gets if ppStagingApi is set in the URL
	 * Valid examples:
	 * https://blk-one01-ishares.musw2.cwp-tst-cloud-origin.blackrock.com/us-ishares/about-etfs/what-is-an-etf?ppStagingApi=t
	 * https://blk-one01-ishares.musw2.cwp-tst-cloud-origin.blackrock.com/us-ishares/about-etfs/what-is-an-etf?ppStagingApi=true
	 * will return true if the query param ppStagingApi is set to t or true
	 *
	 */
	getIsStagingPPApi() {
		const queryString = window.location.search;
		const urlParams = new URLSearchParams(queryString);

		const ppStagingApiParam = urlParams?.get('ppStagingApi');
		return ppStagingApiParam == 't' || ppStagingApiParam == 'true';
	}

	/**
	 * Gets the base url of the products pages api based on the environment set
	 * For production environment we will use only production PP Api
	 * For test environment we will check if staging PP Api requested in the URL to call the staging PP API;otherwise, we will call the test PP API
	 */
	getBaseUrl() {
		return this.isProduction
			? fundCardConfig.prodUrl
			: this.stagingPPApi
			? fundCardConfig.stageUrl
			: fundCardConfig.testUrl;
	}

	/**
	 * Generates the options for AJAX requests, such as the API key being added to the headers
	 */
	generateRequestOptions() {
		return {
			headers: {
				'x-api-key': this.getApiKey(),
			},
		};
	}

	/**
	 * Gets the API key based on if pp staging API requested or not
	 * The xApiKey will be set as a property in the component which will be different depending on the environment
	 * We will use stage-api-key only in case stage PP Api requested and not on production env
	 */
	getApiKey() {
		return this.stagingPPApi && !this.isProduction
			? this.stageApiKey
			: this.xApiKey;
	}

	/**
	 * If ESG data was not already loaded in the main component initialisation, this will load it
	 */
	loadEsgDataIfMissing() {
		if (!this.data.esgAnalytics && !this.esgAjaxRequest) {
			this.esgAjaxRequest = axios.get(
				this.generateUrl('esgAnalytics'),
				this.generateRequestOptions()
			);
			this.esgAjaxRequest.then(
				(response) =>
					(this.data = {
						...this.data,
						esgAnalytics: response.data.productData,
					})
			);
		}
	}

	fetchFundCard() {
		this.doAjaxRequests()
			.then((ajaxFundData) => {
				// dataLinkEvent prop doesn't contain fundName and ticker,
				// because they are available only in the pp api
				// fullDataLink event concatenates all of the strings into one

				const fundName =
					`${ajaxFundData.fundheader?.fundName?.fundName?.formattedValue}` ||
					'';
				const ticker =
					ajaxFundData.fundheader?.fundTicker?.ticker?.formattedValue ||
					ajaxFundData.fundheader?.fundTicker?.listingTicker?.formattedValue;

				const fullDataLinkEvent = `${this.dataLinkEvent} ${fundName} ${
					ticker ? '| ' + ticker : ''
				} | ${this.fundId}`;

				this.data = {
					...ajaxFundData,
					dataLinkEvent: fullDataLinkEvent,
					ticker: ticker,
					...this.data,
				};

				this.hasFetched = true;

				this.loadSuccess.emit({
					data: this.data,
					element: this.el,
				});
			})
			.catch((err) => {
				dsConsole.error(err);
				this.hasFetched = true;
				this.loadError.emit({
					error: err,
					element: this.el,
				});
			});
	}

	@Watch('animateHover')
	onAnimateHoverChange(newState: boolean, prevState: boolean) {
		if (newState !== prevState) {
			this.setHoverAnimation();
		}
	}

	@Listen('tooltipOpen')
	onTooltipOpen() {
		/**
		 * Why delay ?
		 * When some clicks on one tooltip and after that on another one order of events looks like this: open => open => close.
		 * This was source of overlaping bug. Because of that we are waiting for close event and animation and after
		 * that removeAnimations function is safely fired
		 * */
		setTimeout(() => {
			this.removeAnimations();
		}, 100);
	}

	@Listen('tooltipClosed')
	onTooltipClose() {
		this.setHoverAnimation();
	}

	setHoverAnimation() {
		const animationClass = 'ds-animate-hover';
		const cardEl = getChildComponent(this.el, 'ds-card');
		if (cardEl) {
			if (this.animateHover) {
				cardEl.classList.add(animationClass);
			} else {
				cardEl.classList.remove(animationClass);
			}
		}
	}

	removeAnimations() {
		const hoverAnimationClass = 'ds-animate-hover';
		const revealAnimationClass = 'ds-animate-reveal';
		const cardEl = getChildComponent(this.el, 'ds-card');
		if (cardEl && this.animateHover) {
			cardEl.classList.remove(hoverAnimationClass);
			this.el.classList.remove(revealAnimationClass);
			this.el.style.opacity = '1';
		}
	}

	@Method()
	async toggleEsgData(on: boolean): Promise<void> {
		if (on) {
			this.loadEsgDataIfMissing();

			// Clear any existing timeout that would hide the ESG data after 0.5 (to cover if the switch is pressed twice in quick succession)
			if (this.hideEsgDataTimout) {
				clearTimeout(this.hideEsgDataTimout);
			}
			// Ensure ESG data is displaid, even if at first it will have a 0px height.
			this.esgVisibility = 'flex';

			// Changing the layout will kick off the CSS height transition.
			this.layout = FundCardLayoutMode.ESG;
		} else {
			// CSS animate height back to 0
			this.layout = FundCardLayoutMode.Standard;

			// Wait 0.5s for CSS animation to complete, then hide ESG data to prevent tooltips being focusable
			this.hideEsgDataTimout = setTimeout(
				() => (this.esgVisibility = 'none'),
				500
			);
		}
	}

	@Method()
	async getHasFetched() {
		return this.hasFetched;
	}

	handleBuyButtonClick() {
		const modal = document.getElementById(this.modalId) as HTMLDsModalElement;
		if (modal) {
			const finalBuyButton: HTMLDsButtonElement = modal.querySelector(
				'.ds-fund-modal-buy-button'
			);
			if (finalBuyButton && this.data?.ticker) {
				finalBuyButton.href = finalBuyButton.href.replace(
					/\[ticker\]/i,
					this.data.ticker
				);
				modal.open();
			}
		}
	}

	render() {
		const animateReveal =
			this.reveal && !this.isIE && this.revealAnimation !== 'off'
				? ' ds-animate-reveal'
				: '';

		const fundAppearance =
			this.appearance === 'block' ||
			this.appearance === 'primary' ||
			this.appearance === 'standard'
				? 'standard'
				: 'outlined';

		const Tooltip = calculateChildTagName(this.el, 'ds-tooltip');
		const Card = calculateChildTagName(this.el, 'ds-card');
		const Button = calculateChildTagName(this.el, 'ds-button');
		const AddToCompareButton = calculateChildTagName(
			this.el,
			'ds-add-to-compare-button'
		);

		if (this.data)
			return (
				<Host class={`ds-layout-${this.layout} ${animateReveal}`}>
					<CardContainer
						data={this.data}
						appearance={fundAppearance}
						animateCard={this.animateHover}
						esgVisibility={this.esgVisibility}
						Tooltip={Tooltip}
						Card={Card}
						Button={Button}
						AddToCompareButton={AddToCompareButton}
						buyFundUrl={this.buyFundUrl}
						buyButtonText={this.buyButtonText}
						buyButtonAriaLabel={this.buyButtonAriaLabel}
						showBuyButton={this.showBuyButton}
						onBuyButtonClick={() => this.handleBuyButtonClick()}
						showIsaReady={this.showIsaReady}
					/>
				</Host>
			);
	}
}
